import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const navigate = useNavigate();

  const contactMethods = [
    {
      icon: "Phone",
      title: "Appelez-nous",
      value: "+243 818883531",
      description: "Lun-Ven: 8h-18h",
      action: () => window.location.href = 'tel:+243818883531'
    },
    {
      icon: "Mail",
      title: "Email",
      value: "info@orazek.com",
      description: "Réponse sous 24h",
      action: () => window.location.href = 'mailto:info@orazek.com'
    },
    {
      icon: "MessageCircle",
      title: "WhatsApp",
      value: "Chat Direct",
      description: "Disponible maintenant",
      action: () => window.open('https://wa.me/243818883531', '_blank')
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-white dark:bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />
      <div className="absolute inset-0 opacity-0 dark:opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>
      
      <motion.div
        className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/10 rounded-full blur-[150px] mix-blend-screen"
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      {/* CONTAINER ÉLARGI : max-w-[1920px] */}
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-16 xl:gap-32 items-center">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-full">
              <Icon name="Sparkles" size={18} className="text-blue-500 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-600 dark:text-blue-300">Commencez Aujourd'hui</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold text-slate-900 dark:text-white leading-tight">
              Prêt à Transformer <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
                Votre Entreprise ?
              </span>
            </h2>

            <p className="text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
              Rejoignez plus de 500 entreprises qui nous font confiance pour leurs besoins professionnels en RDC. Consultation gratuite et devis personnalisé sous 24 heures.
            </p>

            {/* Benefits List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              {[
                "Consultation gratuite",
                "Devis sous 24 heures",
                "Solutions sur mesure",
                "Support dédié 24/7"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center gap-4 text-slate-700 dark:text-slate-300 text-lg"
                >
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="Check" size={14} className="text-amber-400" />
                  </div>
                  <span>{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 pt-8">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => navigate('/contact')}
                className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 border-none px-8 py-4 text-lg"
              >
                Réserver une Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                onClick={() => window.location.href = 'tel:+243818883531'}
                className="border-slate-300 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 backdrop-blur-sm px-8 py-4 text-lg"
              >
                Appelez Maintenant
              </Button>
            </div>
          </motion.div>

          {/* Right Content - Contact Methods */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {contactMethods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.02, translateX: 10 }}
                onClick={method.action}
                className="bg-slate-50 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl p-8 shadow-2xl cursor-pointer group transition-all duration-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:border-blue-500/30"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Icon name={method.icon} size={30} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-blue-500 dark:text-blue-400 uppercase tracking-widest mb-2">{method.title}</div>
                    <div className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white mb-1 group-hover:text-amber-400 transition-colors">
                      {method.value}
                    </div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">{method.description}</div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-900 transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0">
                    <Icon name="ArrowRight" size={24} />
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 bg-gradient-to-r from-blue-50 dark:from-blue-900/40 to-transparent border-l-4 border-amber-500 rounded-r-xl p-6 backdrop-blur-sm"
            >
              <div className="flex items-center gap-5">
                <div className="bg-amber-500/10 p-3 rounded-full">
                    <Icon name="Award" size={32} className="text-amber-400" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                    Garantie Satisfaction 100%
                  </h4>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    Engagement qualité certifié sur l'ensemble de nos prestations en RDC.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-24 pt-12 border-t border-slate-200 dark:border-white/5 grid grid-cols-2 lg:grid-cols-4 gap-12">
          {[
            { icon: "Clock", value: "< 24h", label: "Temps de Réponse" },
            { icon: "Users", value: "500+", label: "Clients Satisfaits" },
            { icon: "Star", value: "4.9/5", label: "Note Moyenne" },
            { icon: "Headphones", value: "24/7", label: "Support Client" }
          ].map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 dark:bg-slate-800/50 mb-4 text-blue-500 group-hover:text-amber-400 group-hover:scale-110 transition-all duration-300">
                <Icon name={stat.icon} size={32} />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2">{stat.value}</div>
              <div className="text-base text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CTASection;